import { type FC, type ReactNode, useMemo } from 'react'
import classNames from 'classnames'
import { type Location, LocationKind, type LocationRef, type SchedulingEventType } from '../../../../models/Scheduling'
import { type LocationInput } from '../../../../store/scheduling/dto'
import { SelectDropdown, type SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'
import { TimezoneSelector } from '../../../../components/TimezoneSelector/TimezoneSelector'
import { formatTimezoneLabel } from '../../shared/schedulingDisplay'
import { type DefaultLocationKind, defaultOptionLocationRef, isSymbolicLocationKind, resolveDefaultLocations } from './scheduleHelpers'
import { SchedulingDropdownItem } from './SchedulingDropdownItem'
import { dropdownSectionHeader } from './schedulingDropdownOptions'

const _CUSTOM_OPTION_ID = 'custom-location'
const _DEFAULT_PREFIX = 'default:'
const _SAVED_PREFIX = 'saved:'
const _ROW_CLASS = 'px-3 py-1 my-2'
const _EMPTY_CUSTOM: LocationInput = { name: 'Custom location', address: '', note: '', timeZone: '' }

interface LocationSelectProps {
  eventType: SchedulingEventType
  accountDefaultLocationId?: string | null
  userDefaultLocationId?: string | null
  accountLocations: Location[]
  userLocations: Location[]
  ownerUserId: string
  ownerFirstName: string
  isSchedulingForSelf: boolean
  location: LocationRef | null
  customLocation: LocationInput | null
  allowCustomLocation?: boolean
  onChange: (next: { location?: LocationRef | null; customLocation?: LocationInput | null }) => void
}

const locationDisplay = (location: Location): ReactNode => (
  <span className='d-flex flex-column'>
    <span>
      <span className='bold'>{location.name}</span>
      {location.timeZone ? <span className='zocks-text-gray-500'> · {formatTimezoneLabel(location.timeZone)}</span> : null}
    </span>
    <span className='zocks-text-gray-500'>
      {location.address}
      {location.note ? ` · ${location.note}` : ''}
    </span>
  </span>
)

export const LocationSelect: FC<LocationSelectProps> = ({
  eventType,
  accountDefaultLocationId,
  userDefaultLocationId,
  accountLocations,
  userLocations,
  ownerUserId,
  ownerFirstName,
  isSchedulingForSelf,
  location: locationRef,
  customLocation,
  allowCustomLocation = true,
  onChange,
}) => {
  const isCustom = customLocation !== null
  const symbolicKind: DefaultLocationKind | null =
    isCustom || !isSymbolicLocationKind(locationRef?.kind) ? null : locationRef?.kind === LocationKind.ACCOUNT_DEFAULT ? 'account' : 'user'
  const possessive = isSchedulingForSelf ? 'Your' : `${ownerFirstName}'s`
  const { allLocations, accountSection, userSection } = useMemo(() => {
    const byId = new Map<string, Location>()
    accountLocations.forEach((location) => byId.set(location.id, location))
    userLocations.forEach((location) => byId.set(location.id, location))
    const all = [...byId.values()]
    return {
      allLocations: all,
      accountSection: all.filter((location) => !location.ownerUserId),
      userSection: all.filter((location) => location.ownerUserId === ownerUserId),
    }
  }, [accountLocations, userLocations, ownerUserId])
  const defaults = useMemo(
    () => resolveDefaultLocations({ eventType, accountDefaultLocationId, userDefaultLocationId }, allLocations),
    [eventType, accountDefaultLocationId, userDefaultLocationId, allLocations],
  )

  const defaultLabels: Record<DefaultLocationKind, string> = { 'event-type': 'Event type default', account: 'Account default', user: `${possessive} default` }

  const savedOption = (location: Location, group: 'account' | 'user'): SelectOption => ({
    id: `${_SAVED_PREFIX}${group}:${location.id}`,
    label: location.name,
    displayLabel: locationDisplay(location),
    className: classNames(_ROW_CLASS, !isCustom && !symbolicKind && location.id === locationRef?.id && 'zocks-bg-color-primary-light rounded-1'),
  })

  const options: SelectOption[] = []
  if (defaults.length > 0) {
    options.push(dropdownSectionHeader('defaults', 'Use a default'))
    defaults.forEach((entry) =>
      options.push({
        id: `${_DEFAULT_PREFIX}${entry.kind}`,
        label: defaultLabels[entry.kind],
        className: classNames(_ROW_CLASS, symbolicKind === entry.kind && 'zocks-bg-color-primary-light rounded-1'),
        displayLabel: (
          <span>
            {defaultLabels[entry.kind]} <span className='zocks-text-gray-500'>· Currently: {entry.location.name}</span>
          </span>
        ),
      }),
    )
  }
  if (accountSection.length > 0) {
    options.push(dropdownSectionHeader('account', 'Account locations'))
    accountSection.forEach((location) => options.push(savedOption(location, 'account')))
  }
  if (userSection.length > 0) {
    options.push(dropdownSectionHeader('user', `${possessive} locations`))
    userSection.forEach((location) => options.push(savedOption(location, 'user')))
  }

  const symbolicEntry = symbolicKind ? defaults.find((entry) => entry.kind === symbolicKind) : undefined
  const selectedLocation = symbolicKind
    ? (symbolicEntry?.location ?? null)
    : locationRef?.id
      ? (allLocations.find((location) => location.id === locationRef.id) ?? null)
      : null
  const selectedOption: SelectOption | null = isCustom
    ? { id: _CUSTOM_OPTION_ID, label: 'Set custom location' }
    : symbolicKind
      ? { id: `${_DEFAULT_PREFIX}${symbolicKind}`, label: defaultLabels[symbolicKind] }
      : selectedLocation
        ? { id: `${_SAVED_PREFIX}${selectedLocation.id}`, label: selectedLocation.name }
        : null

  const CustomFooter: FC = () => (
    <button
      type='button'
      className='btn btn-link p-2 border-0 zocks-text-primary text-decoration-none w-100 text-start'
      onClick={() => onChange({ location: null, customLocation: { ..._EMPTY_CUSTOM } })}
    >
      Set custom location
    </button>
  )

  const handleOptionSelected = (option: SelectOption) => {
    if (option.id.startsWith(_SAVED_PREFIX)) {
      onChange({ location: { kind: LocationKind.FIXED, id: option.id.slice(option.id.lastIndexOf(':') + 1) }, customLocation: null })
      return
    }
    if (option.id.startsWith(_DEFAULT_PREFIX)) {
      const kind = option.id.slice(_DEFAULT_PREFIX.length)
      const entry = defaults.find((candidate) => candidate.kind === kind)
      if (entry) {
        onChange({ location: defaultOptionLocationRef(entry.kind, eventType, entry.locationId), customLocation: null })
      }
    }
  }

  const updateCustom = (patch: Partial<LocationInput>) => {
    onChange({ location: null, customLocation: { ..._EMPTY_CUSTOM, ...customLocation, ...patch } })
  }

  return (
    <div className='d-flex flex-column gap-3 p-3 border zocks-border-color-gray-200 zocks-border-1.0 position-relative z-2'>
      <div className='d-flex flex-column zocks-gap-0.25'>
        <label className='bold'>Select location</label>
        <div className='paragraph-small zocks-text-gray-600'>Use account defaults or set a custom location</div>
        <SelectDropdown
          options={options}
          initialSelectedOption={selectedOption}
          placeholder='Select location'
          DropDownItemOptionComponent={SchedulingDropdownItem}
          onOptionSelected={handleOptionSelected}
          DropdownFooterComponent={allowCustomLocation ? CustomFooter : undefined}
        />
      </div>
      {isCustom ? (
        <div className='d-flex flex-column zocks-gap-0.5'>
          <div className='input-group'>
            <span className='input-group-text zocks-bg-color-white'>
              <i className='bi bi-geo-alt' aria-hidden='true' />
            </span>
            <input
              type='text'
              className='form-control'
              placeholder='Address'
              value={customLocation?.address ?? ''}
              onChange={(event) => updateCustom({ address: event.currentTarget.value })}
            />
          </div>
          <div className='input-group'>
            <span className='input-group-text zocks-bg-color-white'>
              <i className='bi bi-card-text' aria-hidden='true' />
            </span>
            <input
              type='text'
              className='form-control'
              placeholder='Suite or note (optional)'
              value={customLocation?.note ?? ''}
              onChange={(event) => updateCustom({ note: event.currentTarget.value })}
            />
          </div>
          <TimezoneSelector
            initialSelectedTimezone={customLocation?.timeZone ?? ''}
            onTimezoneSelected={(timezone) => updateCustom({ timeZone: timezone ?? '' })}
          />
          <div className='paragraph-small zocks-text-gray-500 d-flex align-items-center zocks-gap-0.25'>
            <i className='bi bi-calendar-event' aria-hidden='true' />
            Sent in the calendar invite now. Won&apos;t change.
          </div>
        </div>
      ) : selectedLocation ? (
        <div className='d-flex flex-column zocks-gap-0.5'>
          <div className='d-flex align-items-center zocks-gap-0.5 paragraph-small'>
            <i className='bi bi-geo-alt zocks-text-gray-500' aria-hidden='true' />
            {selectedLocation.address}
          </div>
          {selectedLocation.note ? (
            <div className='d-flex align-items-center zocks-gap-0.5 paragraph-small'>
              <i className='bi bi-card-text zocks-text-gray-500' aria-hidden='true' />
              {selectedLocation.note}
            </div>
          ) : null}
          {selectedLocation.timeZone ? (
            <div className='d-flex align-items-center zocks-gap-0.5 paragraph-small'>
              <i className='bi bi-globe zocks-text-gray-500' aria-hidden='true' />
              {formatTimezoneLabel(selectedLocation.timeZone)}
            </div>
          ) : null}
          <div className='paragraph-small zocks-text-gray-500 d-flex align-items-center zocks-gap-0.25'>
            <i className='bi bi-calendar-event' aria-hidden='true' />
            {symbolicKind ? 'The default that applies when the booking is confirmed.' : 'Sent in the calendar invite now. Won’t change.'}
          </div>
        </div>
      ) : null}
    </div>
  )
}
